import type { HTMLAttributes, ReactNode } from 'react'

import { clsx } from 'clsx'

import styles from './CommentItem.module.css'

export type CommentVariant = 'root' | 'context' | 'replyForm' | 'lastReply'

type CommentItemElement = 'article' | 'div' | 'section' | 'aside'

interface CommentItemProps extends Omit<HTMLAttributes<HTMLElement>, 'as'> {
  /**
   * The HTML element type to render. Defaults to 'article'.
   */
  as?: CommentItemElement
  /**
   * Defines the visual/layout flavour of the card. Defaults to `root` (top-level comment).
   */
  variant?: CommentVariant
  /**
   * Node rendered in the left column – usually the avatar + status dot / line.
   */
  avatar: ReactNode
  /**
   * Children rendered in the right (content) column.
   */
  children: ReactNode
}

export const CommentItem = ({
  as: Component = 'article',
  variant = 'root',
  avatar,
  children,
  className,
  ...props
}: CommentItemProps): JSX.Element => {
  return (
    <Component
      {...props}
      className={clsx(styles.card, styles[variant], className)}
      data-testid="comment-item"
    >
      <aside className={styles.avatar}>{avatar}</aside>
      <div className={styles.content}>{children}</div>
    </Component>
  )
}

CommentItem.displayName = 'CommentItem'
